import { useFormContext } from 'react-hook-form';
import FormFieldComponent from '@/components/FormFieldComponent';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useEffect, useState } from 'react';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldSearch from '@/components/FormFieldSearch';
import type {
  ClientGetAllParams,
  ClientResponse,
  ContactGetAllParams,
  ContactGetAllResponse,
  ContactResponse,
} from '@/types/client.types';
import { fetchClient, fetchClients, fetchContacts } from '@/services/clients';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import CreateUpdateClientModal from '../../caseManagement/CreateUpdateClientModal';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import { sortOptionsAlphabetically } from '@/lib/utils';

const SERVICE_TYPE = [{ label: 'Delivery', value: 'DELIVERY' }];

export const DeliveryClientDetails = () => {
  const { control, setValue, getValues, clearErrors, watch } = useFormContext();
  const [openInstructions, setOpenInstructions] = useState(false);
  const [newClient, setNewClient] = useState<undefined | ClientResponse>(undefined);
  const [clientId, setClientId] = useState<string | undefined>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [addNewClient, setAddNewClient] = useState(false);
  const [addNewContact, setAddNewContact] = useState(false);
  const queryClient = useQueryClient();



  const SearchIconsProps = {
    Icon: Search,
    iconProps: { style: { color: '#969696', height: '20px', width: '20px' } },
  };

  const resetValues = () => {
    setValue('clientName', null);
    setValue('contactId', null);
    setValue('contactPhone', null);
    setValue('contactEmail', null);
    setSelectedContact(undefined);
    clearErrors(['clientName', 'contactId', 'contactPhone', 'contactEmail']);
  };

  /* Removed useQuery for clientData as we get data from search result */
  const [selectedClient, setSelectedClient] = useState<ClientResponse | undefined>();
  const [selectedContact, setSelectedContact] = useState<ContactResponse | undefined>();

  /* Prefetch All Clients for Local Search */
  const { data: allClientsData, isLoading: isLoadingClients } = useQuery({
    queryKey: ['allClients'],
    queryFn: () => fetchClients({ pageSize: 1000 }),
    staleTime: 5 * 60 * 1000,
  });

  const allClients = allClientsData?.clients ?? [];

  /* Prefetch Contacts for Selected Client */
  const { data: allContactsData, isLoading: isLoadingContacts } = useQuery({
    queryKey: ['contactsByClient', clientId],
    queryFn: () => {
      if (!clientId) return Promise.reject('No Client ID');
      return fetchContacts({ clientId: clientId, pageSize: 1000 });
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });

  const allContacts = clientId ? (allContactsData?.contacts ?? []) : [];

  /* Resolve Client Logic */
  const watchedClientId = watch('clientId');

  useEffect(() => {
    if (watchedClientId && watchedClientId !== clientId) {
      setClientId(watchedClientId);
    }
  }, [getValues('clientId')]);

  useEffect(() => {
    if (selectedClient) {
      const client = selectedClient;
      setValue('clientName', client.clientName, {
        shouldValidate: true,
      });

      const isClientPrepaid = (client as any).isPrepaid;
      setValue('isPrepaid', isClientPrepaid || false);
      setOpenInstructions(true);
    }
  }, [selectedClient]);

  useEffect(() => {
    if (newClient) {
      setClientId(newClient?.clientId);
      setSelectedClient(newClient);
      setValue('clientId', newClient?.clientId, {
        shouldValidate: true,
      });
      setValue('clientName', newClient?.clientName);
      setValue('isPrepaid', (newClient as any).isPrepaid || false);
      setOpenInstructions(true);
      setRefreshKey((prev) => prev + 1);
      resetValues();
    }
  }, [newClient]);

  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  const { data: resolvedClientData } = useQuery({
    queryKey: ['resolveClient', watchedClientId],
    queryFn: async () => {
      if (!watchedClientId) return null;
      // If it looks like a valid UUID, fetch by ID
      if (uuidRegex.test(watchedClientId)) {
        try {
          return await fetchClient(watchedClientId);
        } catch (e) {
          // Fallback or ignore if not found
          return null;
        }
      } else {
        // Assume it is a code, search by code
        try {
          // Local search attempt first for speed?
          const localMatch = allClients.find(c => c.code === watchedClientId);
          if (localMatch) return localMatch;

          const res = await fetchClients({ code: watchedClientId, pageSize: 10 });
          return res.clients?.[0] ?? null;
        } catch (e) {
          return null;
        }
      }
    },
    enabled: !!watchedClientId && (!selectedClient || (selectedClient.clientId !== watchedClientId && selectedClient.code !== watchedClientId)),
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (resolvedClientData) {
      setSelectedClient(resolvedClientData);
      setValue('clientName', resolvedClientData.clientName, { shouldValidate: true });

      // If the form had the code, update it to UUID
      if (watchedClientId !== resolvedClientData.clientId) {
        setValue('clientId', resolvedClientData.clientId, { shouldValidate: true });
      }

      setValue('isPrepaid', (resolvedClientData as any).isPrepaid || false);
      setOpenInstructions(true);
    }
  }, [resolvedClientData]);

  useEffect(() => {
    if (!getValues('type')) {
      setValue('type', 'DELIVERY', { shouldValidate: true });
    }
  }, []);

  return (
    <div className="flex flex-col w-full mt-11 gap-6">

      {/* Service Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormFieldSelect name="type" label="Service Type" required options={SERVICE_TYPE} placeholder="Select Service Type" defaultValue="DELIVERY" disabled />
      </div>

      {/* Client Search and Name */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<ClientGetAllParams>
          name="clientId"
          key={refreshKey}
          required
          label="Client #"
          placeholder="Search Client Code"
          props={SearchIconsProps}
          searchFieldName="code"
          api={(params) => {
            const searchTerm = (params.code || '').toLowerCase();
            const filtered = allClients.filter(c =>
              c.code.toLowerCase().includes(searchTerm) ||
              (c.clientName || '').toLowerCase().includes(searchTerm) ||
              (c.clientId || '').toLowerCase().includes(searchTerm)
            );

            // Add selected client if missing from filtered list but matches
            if (selectedClient && !filtered.find(c => c.clientId === selectedClient.clientId)) {
              if (selectedClient.code.toLowerCase().includes(searchTerm)) {
                filtered.unshift(selectedClient);
              }
            }

            const values = sortOptionsAlphabetically(
              filtered.map(client => ({
                label: client.code + ' - ' + client.clientName,
                value: client.clientId,
                displayLabel: client.code,
                option: { data: client }
              })),
              (option) => option.label
            );

            return { values, isLoading: isLoadingClients };
          }}
          onChange={(e: string, option: any) => {
            if (clientId && clientId === e) return;
            resetValues();
            setClientId(e);
            if (option?.data) {
              const client = option.data as ClientResponse;
              setSelectedClient(client);
              setValue('clientName', client.clientName, { shouldValidate: true });
              setValue('isPrepaid', (client as any).isPrepaid || false);
              setOpenInstructions(true);
            } else {
              setSelectedClient(undefined);
            }
          }}
          addNewOption={{
            label: 'Add New Client',
            onNewOptionClick: () => {
              setAddNewClient(true);
            },
          }}
          onSearchChange={() => {
            setNewClient(undefined);
            setClientId(undefined);
            setSelectedClient(undefined);
            resetValues();
          }}
        />
        <FormFieldComponent
          control={control}
          name="clientName"
          label="Client Name"
          placeholder="Client Name"
          required
          disabled
          disabledStyle="bg-[#F5F5F5]"
        />
      </div>

      {/* Client Instructions (Collapsible) */}
      {selectedClient && (
        <Collapsible
          className={`w-full bg-[#F5F5F5] flex flex-col rounded-md font-semibold justify-between gap-2 ${openInstructions && 'border-[1px] border-[#A41919]'}`}
          open={openInstructions}
        >
          <CollapsibleTrigger
            className={`flex justify-between items-center w-full rounded-md bg-[#F5F5F5] py-3 px-4 focus:outline-none ${!openInstructions && 'focus:border-[1px] focus:border-focuscolor'}`}
            onClick={() => setOpenInstructions(!openInstructions)}
          >
            <span>Instructions</span>
            {openInstructions ? <ChevronUp color="#969696" /> : <ChevronDown color="#969696" />}
          </CollapsibleTrigger>
          <CollapsibleContent className="bg-[#F5F5F5] p-4 rounded-md">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="font-medium">Client Instructions</span>
                <span className="font-medium text-[#676767] text-sm">
                  {(selectedClient as any)?.instructions ?? 'No Instructions Available'}
                </span>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Case Name and File/Reference Number */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormFieldComponent
          control={control}
          name="caseName"
          label="Case Name"
          placeholder="Enter Case Name"
          maxLength={128}
        />
        <FormFieldComponent
          control={control}
          name="fileReferenceNumber"
          label="File/Reference #"
          placeholder="Enter File or Reference Number"
          maxLength={20}
        />
      </div>

      {/* Client Contact */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-black">
        <FormFieldSearch<ContactGetAllParams>
          name="contactId"
          key={refreshKey}
          placeholder="Search Client's Contact"
          label="Client's Contact"
          props={SearchIconsProps}
          required
          searchFieldName="search"
          disabled={!clientId}
          api={(params) => {
            const searchTerm = (params.search || '').toLowerCase();
            const filtered = allContacts.filter(c => {
              const fullName = (c.firstName + ' ' + c.lastName).toLowerCase();
              return fullName.includes(searchTerm);

            });

            if (selectedContact && !filtered.find((c) => c.contactId === selectedContact.contactId)) {
              const fullName = (selectedContact.firstName + ' ' + selectedContact.lastName).toLowerCase();
              if (fullName.includes(searchTerm)) {
                filtered.unshift(selectedContact);
              }
            }

            const values = sortOptionsAlphabetically(
              filtered.map(contact => ({
                label: contact.firstName + ' ' + contact.lastName,
                value: contact.contactId || '',
                option: contact
              })),
              (option) => option.label
            );

            return { values, isLoading: isLoadingContacts };
          }}
          onChange={(e: string, selectedOption) => {
            setValue('contactId', e, { shouldValidate: true });
            if (selectedOption) {
              const contact = selectedOption as any;
              setSelectedContact(contact);
              // Store contact details for use in Step 2
              const contactName = contact.name || contact.contactName || (contact.firstName && contact.lastName ? `${contact.firstName} ${contact.lastName}` : '');
              setValue('contactName', contactName);
              setValue('contactPhone', contact.phone || contact.phoneNumber || null);
              setValue('contactEmail', contact.email || contact.emailAddress || null);
            } else {
              setSelectedContact(undefined);
            }
          }}
          addNewOption={
            clientId
              ? {
                label: 'Add New Contact',
                onNewOptionClick: () => {
                  setAddNewContact(true);
                },
              }
              : undefined
          }
        />
        <FormFieldComponent
          control={control}
          name="contactPhone"
          label="Contact's Phone Number"
          placeholder="Contact's Phone Number"
          disabled
          disabledStyle="bg-[#F5F5F5]"
        />
        <FormFieldComponent
          control={control}
          name="contactEmail"
          label="Contact's Email Address"
          placeholder="Contact's Email Address"
          disabled
          disabledStyle="bg-[#F5F5F5]"
        />
      </div>

      {/* Pre-paid Checkbox */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormFieldCheckbox
          control={control}
          name="isPrepaid"
          label="Pre-paid"
          description=""
        />
      </div>

      {/* Add New Client Modal */}
      {addNewClient && (
        <CreateUpdateClientModal
          onClose={() => setAddNewClient(false)}
          operation="CREATE_CLIENT"
          onUpdate={(client) => {
            setNewClient(client);
            setAddNewClient(false);
          }}
        />
      )}

      {/* Add New Contact Modal */}
      {addNewContact && (
        <CreateUpdateClientModal
          operation="UPDATE_CONTACT"
          clientId={clientId}
          onClose={() => setAddNewContact(false)}
          onUpdate={(updatedClient) => {
            if (clientId && updatedClient.contacts) {
              queryClient.setQueryData<ContactGetAllResponse>(['contactsByClient', clientId], (old) => ({
                currentPage: 1,
                totalPages: 1,
                ...old,
                contacts: updatedClient.contacts ?? [],
                totalRows: updatedClient.contacts?.length ?? 0,
              }));
            }
            setRefreshKey((prev) => prev + 1);
            setAddNewContact(false);
            if (updatedClient && updatedClient.contacts && updatedClient.contacts.length > 0) {
              const sortedContacts = [...updatedClient.contacts].sort((a: ContactResponse, b: ContactResponse) => {
                // Ensure sorting works even if createdAt is string or Date
                const dateA = new Date(a.createdAt ?? 0).getTime();
                const dateB = new Date(b.createdAt ?? 0).getTime();
                return dateB - dateA;
              });
              const newestContact = sortedContacts[0];
              setSelectedContact(newestContact);
              setValue('contactId', newestContact.contactId, { shouldValidate: true });
              const nc = newestContact as any;
              setValue('contactName', nc.name || nc.contactName || (newestContact.firstName && newestContact.lastName ? `${newestContact.firstName} ${newestContact.lastName}` : ''));
              setValue('contactPhone', nc.phone || nc.phoneNumber || null);
              setValue('contactEmail', nc.email || nc.emailAddress || null);
            }
          }}
        />
      )}
    </div>
  );
};
